
import { useEffect, useState, useCallback } from "react";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { loadStripe, Stripe, StripeError } from "@stripe/stripe-js";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface OrderAddress {
  street: string;
  city: string;
  state: string;
  postalCode: string;
}

interface OrderContact {
  firstName: string;
  lastName: string;
  phone: string;
}

interface OrderDetails {
  deliveryType: "delivery" | "pickup";
  address: OrderAddress | null;
  specialInstructions: string;
  contact: OrderContact;
}

interface StripePaymentProps {
  amount: number;
  items: any[];
  onSuccess: (orderId: string) => void;
  orderDetails: OrderDetails;
}

let stripePromise: Promise<Stripe | null> | null = null;

const getStripePromise = async () => {
  if (!stripePromise) {
    console.log("Getting Stripe key...");
    const { data, error } = await supabase.functions.invoke('get-stripe-key');
    if (error) {
      console.error("Error getting Stripe key:", error);
      throw error;
    }
    if (!data?.publishableKey) {
      throw new Error("No Stripe publishable key returned");
    }
    
    const isLiveMode = data.mode === 'live';
    console.log(`Loading Stripe with ${isLiveMode ? 'LIVE' : 'TEST'} mode publishable key`);
    
    stripePromise = loadStripe(data.publishableKey);
    return { stripe: await stripePromise, mode: data.mode };
  }
  return { stripe: await stripePromise, mode: 'unknown' };
};

const PaymentForm = ({ 
  orderId, 
  paymentIntentId, 
  onSuccess,
  stripeMode
}: { 
  orderId: string; 
  paymentIntentId: string;
  onSuccess: (orderId: string) => void;
  stripeMode: 'live' | 'test' | 'unknown';
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate('/auth');
      return false;
    }
    return true;
  };

  const processPaymentSuccess = async () => {
    console.log("Processing payment success:", { orderId, paymentIntentId });

    try {
      const { data, error: updateError } = await supabase.functions.invoke('process-payment-success', {
        body: { 
          orderId,
          paymentIntentId
        }
      });

      if (updateError) {
        console.error("Error processing payment success:", updateError);
        throw new Error("Failed to process payment success");
      }

      if (!data) {
        console.error("No data returned from payment success processing");
        throw new Error("Failed to get response from payment processor");
      }
      
      if (data.error) {
        console.error("Error in payment success response:", data.error);
        
        // Handle the case where transaction exists but order doesn't
        if (data.partialSuccess) {
          console.log("Payment recorded but order not found:", data);
          toast({
            title: "Payment Recorded",
            description: "Your payment was successful, but we're having trouble updating your order. Our team will resolve this shortly.",
            variant: "warning",
            duration: 10000
          });
          // Still consider this a success but with a warning
          return data;
        }
        
        // Check if we found an order with a different ID
        if (data.correctOrderId) {
          console.log("Found order with different ID:", data.correctOrderId);
          // Use the correct order ID from the database
          return await processPaymentSuccessWithCorrectedOrderId(data.correctOrderId);
        }
        
        throw new Error(data.error || "Failed to process payment");
      }

      console.log("Payment success processing response:", data);
      return data;
    } catch (err) {
      console.error("Error in processPaymentSuccess:", err);
      throw err;
    }
  };

  const processPaymentSuccessWithCorrectedOrderId = async (correctOrderId: string) => {
    console.log("Retrying with correct order ID:", correctOrderId);
    try {
      const { data, error } = await supabase.functions.invoke('process-payment-success', {
        body: { 
          orderId: correctOrderId,
          paymentIntentId
        }
      });

      if (error || !data || data.error) {
        console.error("Error in corrected order ID processing:", error || data?.error);
        throw new Error("Failed with corrected order ID");
      }

      console.log("Successfully processed with corrected order ID:", data);
      return { ...data, orderId: correctOrderId };
    } catch (err) {
      console.error("Error in processPaymentSuccessWithCorrectedOrderId:", err);
      throw err;
    }
  };

  const sendChefNotification = async () => {
    try {
      console.log("Sending chef notification for order:", orderId);
      await supabase.functions.invoke('send-chef-notifications', {
        body: { 
          orderId,
          type: 'new-order'
        }
      });
    } catch (err) {
      console.error("Error in sendChefNotification:", err);
      // Non-critical error, don't throw
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isLoading) {
      console.log("Payment submission already in progress");
      return;
    }

    const isAuthenticated = await checkAuth();
    if (!isAuthenticated) return;

    if (!stripe || !elements) {
      console.log("Stripe not initialized:", { stripe, elements });
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      console.log("Starting payment confirmation for intent:", paymentIntentId);
      
      const result = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
        confirmParams: {
          return_url: `${window.location.origin}/order-confirmation?orderId=${orderId}`,
        }
      });

      if (result.error) {
        console.error("Payment confirmation error:", result.error);
        throw result.error;
      }

      if (!result.paymentIntent) {
        throw new Error("Payment failed - no payment intent returned");
      }

      console.log("Payment confirmed:", {
        paymentIntentId: result.paymentIntent.id,
        status: result.paymentIntent.status
      });

      if (result.paymentIntent.status !== 'succeeded') {
        throw new Error(`Payment failed - status is ${result.paymentIntent.status}`);
      }

      // Process the payment success to update order status
      const processResult = await processPaymentSuccess();
      console.log("Payment success processed", processResult);

      // Use the potentially corrected order ID
      const finalOrderId = processResult.orderId || orderId;
      
      // Optional chef notification - don't block on this
      try {
        await sendChefNotification();
      } catch (notifyErr) {
        console.error("Chef notification failed but payment was successful:", notifyErr);
      }

      toast({
        title: "Payment Successful",
        description: "Your order has been placed successfully!",
        key: "payment-success" // Add unique key to prevent duplicates
      });

      onSuccess(finalOrderId);
    } catch (err: any) {
      console.error("Payment error:", err);
      setError(err.message || 'An error occurred during payment');
      
      // Special handling for payment success but order processing error
      if (err.message && err.message.includes("payment was successful")) {
        toast({
          title: "Payment Processed",
          description: "Your payment was successful, but there was an issue with your order. Our team is reviewing this and will contact you shortly.",
          variant: "warning",
          duration: 10000,
          key: "payment-partial-success"
        });
        
        // Navigate to home rather than staying on the error page
        navigate('/');
        return;
      }
      
      toast({
        title: "Error",
        description: err.message || 'An error occurred during payment',
        variant: "destructive",
        key: "payment-error" // Add unique key for error
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePaymentElementLoad = (event: { elementType: "payment"; error: StripeError }) => {
    if (event.error) {
      console.error("Payment element load error:", event.error);
      setError(event.error.message || "Payment element failed to load");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      <div className="min-h-[200px] bg-white rounded-md p-4">
        <PaymentElement onLoadError={handlePaymentElementLoad} />
      </div>
      
      <Button 
        type="submit" 
        disabled={!stripe || !elements || isLoading}
        className="w-full"
      >
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Processing...
          </>
        ) : (
          'Pay Now'
        )}
      </Button>
    </form>
  );
};

export const StripePayment = ({ amount, items, onSuccess, orderDetails }: StripePaymentProps) => {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [stripeReady, setStripeReady] = useState(false);
  const [stripeMode, setStripeMode] = useState<'live' | 'test' | 'unknown'>('unknown');
  const navigate = useNavigate();
  const [isInitializing, setIsInitializing] = useState(false);
  const { toast } = useToast();

  const createPaymentIntent = useCallback(async () => {
    if (!stripeReady || isInitializing) return;
    
    try {
      setIsInitializing(true);
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/auth');
        return;
      }

      console.log("Creating checkout session with process-payment...");
      console.log("orderDetails deliveryType:", orderDetails.deliveryType);
      
      const itemsWithBuyer = items.map(item => ({
        ...item,
        buyer_id: session.user.id
      }));

      // Prepare items with proper field mappings
      const processedItems = itemsWithBuyer.map(item => {
        const processedItem = {
          ...item,
          scheduled_date: item.scheduledDate,
        };

        if (item.preparation_type === 'on_demand') {
          processedItem.scheduled_time = `${item.pickup_window_start} - ${item.pickup_window_end}`;
        } else {
          processedItem.scheduled_time = item.scheduledTime;
        }

        return processedItem;
      });
      
      // Use the process-payment function with customized options for embedded payment
      const { data, error } = await supabase.functions.invoke('process-payment', {
        body: { 
          items: processedItems, 
          orderDetails,
          idempotencyKey: `${session.user.id}-${Date.now()}`,
          embedded: true // Add flag to indicate we want embedded payment
        }
      });

      if (error) {
        console.error("Error creating payment session:", error);
        throw new Error(`Failed to send a request to the Edge Function: ${error.message}`);
      }

      console.log("Full response from process-payment:", data);

      // Handle either clientSecret (for embedded) or URL (for redirect)
      if (data?.clientSecret) {
        console.log("Received clientSecret for embedded payment");
        setClientSecret(data.clientSecret);
        setOrderId(data.orderId);
        setPaymentIntentId(data.paymentIntentId);
      } else if (data?.payment_intent_client_secret) {
        // Alternative field name that might be used
        console.log("Received payment_intent_client_secret for embedded payment");
        setClientSecret(data.payment_intent_client_secret);
        setOrderId(data.orderId);
        setPaymentIntentId(data.paymentIntentId);
      } else if (data?.url) {
        // Redirect flow as fallback
        console.log("Received redirect URL, using redirect flow");
        window.location.href = data.url;
      } else if (data?.orderId && data?.paymentIntentId) {
        console.error("Missing clientSecret in response but got orderId and paymentIntentId:", data);
        throw new Error("Server returned order info without payment credentials");
      } else {
        console.error("Invalid response data:", data);
        throw new Error("Invalid response from payment processing");
      }
    } catch (err: any) {
      console.error("Error creating payment:", err);
      setError(err.message || "An unknown error occurred");
      if (err.message && err.message.includes("buyer_id")) {
        navigate('/auth');
      }
    } finally {
      setIsInitializing(false);
    }
  }, [items, orderDetails, stripeReady, navigate, isInitializing]);

  useEffect(() => {
    const checkAuthAndInit = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/auth');
        return;
      }

      try {
        const { stripe, mode } = await getStripePromise();
        setStripeReady(true);
        setStripeMode(mode as 'live' | 'test' | 'unknown');
        
        if (mode === 'test') {
          toast({
            title: "Test Mode Detected",
            description: "Stripe is running in TEST mode. Use test cards for payments.",
            variant: "warning",
            duration: 6000
          });
        }
      } catch (err: any) {
        console.error("Error initializing Stripe:", err);
        setError(err.message || "Failed to initialize payment system");
      }
    };
    
    checkAuthAndInit();
  }, [navigate, toast]);

  useEffect(() => {
    if (stripeReady && !clientSecret && !isInitializing) {
      createPaymentIntent();
    }
  }, [stripeReady, clientSecret, createPaymentIntent, isInitializing]);

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Payment Error</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!stripeReady || !clientSecret || !orderId || !paymentIntentId) {
    return (
      <div className="flex items-center justify-center p-6">
        <Loader2 className="h-6 w-6 animate-spin" />
        <span className="ml-2">Loading payment form...</span>
      </div>
    );
  }

  return (
    <>
      {stripeMode === 'test' && (
        <Alert variant="warning" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Test Mode Active</AlertTitle>
          <AlertDescription>
            Stripe is running in TEST mode. No real charges will be made.
            Use test card 4242 4242 4242 4242 (any future date, any CVC).
          </AlertDescription>
        </Alert>
      )}
      
      <Elements 
        stripe={stripePromise}
        options={{
          clientSecret,
          appearance: {
            theme: 'stripe',
            variables: {
              colorPrimary: '#0570de',
              colorBackground: '#ffffff',
              colorText: '#30313d',
              colorDanger: '#df1b41',
              fontFamily: 'system-ui, sans-serif',
              spacingUnit: '4px',
              borderRadius: '4px',
            },
          },
        }}
      >
        <PaymentForm 
          orderId={orderId} 
          paymentIntentId={paymentIntentId} 
          onSuccess={onSuccess}
          stripeMode={stripeMode}
        />
      </Elements>
    </>
  );
};
